import { useEffect, useRef, useCallback } from "react";
import req from "@/utils/req";
import { UseMonitoramentoHeartbeatOptions } from "./types";
import { caminhoSemQuery, socketIdDaWeb } from "./identificador";

/**
 * Presença do painel: entrada ao montar, um sinal periódico **só com o separador à vista**, e a
 * saída ao fechar.
 *
 * - O sinal pára com `document.hidden`: «online» queria dizer «tem um separador aberto algures»
 *   (A06). Ao voltar à vista manda-se um sinal logo.
 * - Todos os pedidos levam o `socket_id` deste browser — no corpo, porque o `sendBeacon` da saída
 *   não manda cabeçalhos (A07).
 * - A página vai sem query string (A19); o servidor só a conta quando muda (A08).
 * - Falhas não incomodam quem trabalha: ficam na consola.
 * - Uma entrada falhada **não** cala o sinal: o servidor abre a sessão no primeiro sinal que
 *   chegar. Antes, uma falha de rede no arranque deixava a página inteira sem presença. Só uma
 *   recusa (403 — a empresa sem o módulo, ou sem administrador a quem atribuir) pára o sinal.
 */
export const useMonitoramentoHeartbeat = (options: UseMonitoramentoHeartbeatOptions) => {
    const {
        fonteCodigo,
        basePath,
        intervaloMs = 30000,
        enabled = true,
        onError,
    } = options;

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    /** Há uma sessão aberta no servidor — é o que decide se a saída se manda. */
    const isRegisteredRef = useRef<boolean>(false);
    /** O servidor recusou (403): não vale a pena insistir de 30 em 30 segundos. */
    const recusadoRef = useRef<boolean>(false);

    const lerResposta = useCallback((res: any, context: string) => {
        if (res?.data?.estado === "ok") {
            isRegisteredRef.current = true;
            return;
        }
        if (res?.status === 403) recusadoRef.current = true;
        console.warn(`[Monitoramento] ${context}:`, res?.data?.texto);
        if (onError) onError(new Error(res?.data?.texto ?? context));
    }, [onError]);

    const handleError = useCallback((error: unknown, context: string) => {
        console.warn(`[Monitoramento] Erro em ${context}:`, error);
        if (onError) onError(error as Error);
    }, [onError]);

    const corpo = useCallback((extra: Record<string, string> = {}) => ({
        fonte_codigo: fonteCodigo,
        socket_id: socketIdDaWeb(fonteCodigo),
        ...extra,
    }), [fonteCodigo]);

    const registarEntrada = useCallback(async () => {
        try {
            const res = await req().post(`${basePath}/monitoramento/presenca/entrada`, corpo({
                pagina_actual: caminhoSemQuery(window.location.href),
            }));
            lerResposta(res, "registar entrada");
        } catch (error) {
            handleError(error, "registar entrada");
        }
    }, [basePath, corpo, handleError, lerResposta]);

    const registarSaida = useCallback(() => {
        if (!isRegisteredRef.current) return;

        try {
            const formData = new FormData();
            Object.entries(corpo()).forEach(([chave, valor]) => formData.append(chave, valor));

            const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
            if (csrfToken) formData.append("_token", csrfToken);

            navigator.sendBeacon(`${basePath}/monitoramento/presenca/saida`, formData);
        } catch (error) {
            console.warn("[Monitoramento] Erro ao registar saída:", error);
        }
    }, [basePath, corpo]);

    const sendHeartbeat = useCallback(async () => {
        if (recusadoRef.current || document.hidden) return;

        try {
            const res = await req().post(`${basePath}/monitoramento/presenca/heartbeat`, corpo({
                pagina_actual: caminhoSemQuery(window.location.href),
                pagina_titulo: document.title,
            }));
            lerResposta(res, "enviar heartbeat");
        } catch (error) {
            handleError(error, "enviar heartbeat");
        }
    }, [basePath, corpo, handleError, lerResposta]);

    useEffect(() => {
        if (!enabled) return;

        registarEntrada();
        intervalRef.current = setInterval(sendHeartbeat, intervaloMs);

        const handleBeforeUnload = () => registarSaida();
        const handleVisibilityChange = () => {
            if (!document.hidden) sendHeartbeat();
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            registarSaida();
            window.removeEventListener("beforeunload", handleBeforeUnload);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [enabled, intervaloMs, registarEntrada, registarSaida, sendHeartbeat]);

    const forceHeartbeat = useCallback(() => {
        sendHeartbeat();
    }, [sendHeartbeat]);

    // Uma função e não o valor: o valor era o da ref no momento do render, sempre velho.
    const estaRegistado = useCallback(() => isRegisteredRef.current, []);

    return {
        forceHeartbeat,
        estaRegistado,
    };
};

export default useMonitoramentoHeartbeat;
